import { admin2 } from '@/api/instance'

/**
 * @template T
 * @typedef {Promise<[T, string|null]>} Api2Response
 */

export const RegisterShopRebate = async ({
  shopId,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/shopPoint/register`,
  })
}

export const FindShopPoint = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPoint`,
  })
}

export const UpdateShopPoint = async ({
  shopId,
  name,
  unitName,
}) => {
  return await admin2({
    method: 'PUT',
    url: `/${shopId}/shopPoint`,
    data: {
      name,
      unitName,
    },
  })
}

export const GetShopPointYearExpiration = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPoint/yearExpiration`,
  })
}

export const UpdateShopPointYearExpiration = async ({
  shopId,
  daysUntilExpiration,
  daysDiffExpirationNotify,
}) => {
  return await admin2({
    method: 'PUT',
    url: `/${shopId}/shopPoint/yearExpiration`,
    data: {
      daysUntilExpiration,
      daysDiffExpirationNotify,
    },
  })
}

export const FindMemberPointYearExpirations = async ({
  shopId,
  memberId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/member/${memberId}/pointWalletYearExpirations`,
    params: {
      shopPointKey,
    },
  })
}

export const AddPointWalletRecord = async ({
  shopId,
  memberId,
  addAmount,
  originMessage,
  branchId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/member/${memberId}/addPointWalletRecord`,
    data: {
      addAmount,
      originMessage,
      branchId,
      shopPointKey,
    },
  })
}

export const UsePointWalletRecord = async ({
  shopId,
  memberId,
  useAmount,
  originMessage,
  branchId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/member/${memberId}/usePointWalletRecord`,
    data: {
      useAmount,
      originMessage,
      branchId,
      shopPointKey,
    },
  })
}

export const FindPointWalletRecord = async ({
  shopId,
  memberId,
  start,
  limit,
  shopPointKey,
  hasRemainingBalance,
  notExpired,
  operationShopId,
}) => {
  return await admin2({
    method: 'get',
    url: `/${shopId}/member/${memberId}/pointWalletRecord`,
    params: {
      start,
      limit,
      shopPointKey,
      hasRemainingBalance,
      notExpired,
      operationShopId,
    },
  })
}

export const FindPointWalletRecordCount = async ({
  shopId,
  memberId,
  shopPointKey,
  hasRemainingBalance,
  notExpired,
  operationShopId,
}) => {
  return await admin2({
    method: 'get',
    url: `/${shopId}/member/${memberId}/pointWalletRecord/count`,
    params: {
      shopPointKey,
      hasRemainingBalance,
      notExpired,
      operationShopId,
    },
  })
}

/**
 * 查詢會員點數餘額明細（單點到期）admin.member.findOnePointActiveDetails
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.memberId - 會員ID
 * @param {string} params.shopPointKey - 點數錢包 Key
 * @returns {Promise<{totalBalance: number, items: Array<{expirationDate: string, totalRemaining: number}>}>} 點數餘額明細，包含總餘額與各到期日的剩餘點數
 * @example
 * // Response:
 * // {
 * //   totalBalance: 1500,
 * //   items: [
 * //     { expirationDate: "2026-03-15", totalRemaining: 500 },
 * //     { expirationDate: "2026-04-01", totalRemaining: 300 },
 * //   ]
 * // }
 */
export const FindMemberPointActiveDetails = async ({
  shopId,
  memberId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'get',
    url: `/${shopId}/member/${memberId}/pointActiveDetails`,
    params: { shopPointKey },
  })
}

/**
 * 查詢第三方點數錢包記錄 admin.member.findExternalPointWalletRecord
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.memberId - 會員ID
 * @param {string} params.shopPointKey - 點數錢包 Key
 * @param {number} [params.start=0] - 查詢起始位置
 * @param {number} [params.limit=20] - 查詢筆數
 * @returns {Api2Response<Array>} 第三方點數紀錄列表，包含 id, operation, userId, uniqueId, origin, originId, originMessage, amount, PointClientId, createdAt, updatedAt
 */
export const FindExternalPointWalletRecord = async ({
  shopId,
  memberId,
  shopPointKey,
  start,
  limit,
  operationShopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/member/${memberId}/externalPointWalletRecords`,
    params: {
      shopPointKey,
      start,
      limit,
      operationShopId,
    },
  })
}

/**
 * 統計第三方點數錢包記錄數量 admin.member.countExternalPointWalletRecord
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.memberId - 會員ID
 * @param {string} params.shopPointKey - 點數錢包 Key
 * @returns {Api2Response<{ count: number }>} 回傳 { count: number }
 */
export const CountExternalPointWalletRecord = async ({
  shopId,
  memberId,
  shopPointKey,
  operationShopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/member/${memberId}/externalPointWalletRecords/count`,
    params: {
      shopPointKey,
      operationShopId,
    },
  })
}

/**
 * 取得點數總額報表 admin.shopPointReport.getOriginTotalAmount
 * @param {Object} params - 查詢參數
 * @param {string} params.shopId - 商店ID
 * @param {string} params.createStart - 開始日期
 * @param {string} params.createEnd - 結束日期
 * @param {string} [params.shopPointKey] - 點數錢包 Key
 * @returns {Promise<Object>} 點數總額資訊
 */
export const GetOriginTotalAmount = async ({
  shopId,
  createStart,
  createEnd,
  shopPointKey,
}) => {
  const [res, err, rawErr] = await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/originTotalAmount`,
    params: {
      createStart,
      createEnd,
      shopPointKey,
    },
  })
  if (rawErr?.errorCode === 'EXTERNAL_POINT_SERVICE_NOT_FOUND') {
    return [null, '點數報表不支援第三方點數', rawErr]
  }
  return [res, err, rawErr]
}

export const GetOriginTotalAmountByBranch = async ({
  shopId,
  createStart,
  createEnd,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/originTotalAmountByBranch`,
    params: {
      createStart,
      createEnd,
    },
  })
}

// report
export const GetPointRecord = async ({
  shopId,
  createStart,
  createEnd,
  start,
  limit,
  origin,
  memberId,
  shopPointKey,
  operationShopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/records`,
    params: {
      createStart,
      createEnd,
      start,
      limit,
      origin,
      memberId,
      shopPointKey,
      operationShopId,
    },
  })
}

export const GetPointRecordCount = async ({
  shopId,
  createStart,
  createEnd,
  origin,
  memberId,
  shopPointKey,
  operationShopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/records/count`,
    params: {
      createStart,
      createEnd,
      origin,
      memberId,
      shopPointKey,
      operationShopId,
    },
  })
}

// 新增給點連結活動
export const CreateShopPointLink = async ({
  shopId,
  name,
  hasEffectivePeriod,
  point,
  shopPointKey,
  description,
  startAt,
  endAt,
  imageId,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/shopPointLink`,
    data: {
      name,
      hasEffectivePeriod,
      point,
      shopPointKey,
      description,
      startAt,
      endAt,
      imageId,
    },
  })
}
// 取得點數連結活動列表
export const GetShopPointLink = async ({
  shopId,
  start,
  limit,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointLink`,
    params: {
      start,
      limit,
    },
  })
}
// 取得點數連結活動數量
export const GetShopPointLinkCount = async ({
  shopId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointLink/count`,
  })
}
// 取得點數連結活動
export const FindShopPointLink = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointLink/${id}`,
  })
}
// 更新點數連結活動
export const UpdateShopPointLink = async ({
  shopId,
  id,
  name,
  hasEffectivePeriod,
  description,
  startAt,
  endAt,
  imageId,
}) => {
  return await admin2({
    method: 'PUT',
    url: `/${shopId}/shopPointLink/${id}`,
    data: {
      name,
      hasEffectivePeriod,
      description,
      startAt,
      endAt,
      imageId,
    },
  })
}
// 刪除點數連結活動
export const DeleteShopPointLink = async ({
  shopId,
  id,
}) => {
  return await admin2({
    method: 'DELETE',
    url: `/${shopId}/shopPointLink/${id}`,
  })
}
// 取得點數連結序號列表
export const GetShopPointLinkCode = async ({
  shopId,
  ShopPointLinkId,
  start,
  limit,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointLinkCode`,
    params: {
      start,
      limit,
      ShopPointLinkId,
    },
  })
}
// 取得點數連結序號數量
export const GetShopPointLinkCodeCount = async ({
  shopId,
  ShopPointLinkId,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointLinkCode/count`,
    params: {
      ShopPointLinkId,
    },
  })
}
// 批次新增給點連結活動
export const CreateShopPointLinkCode = async ({
  shopId,
  shopPointLinkId,
  amount,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/shopPointLinkCode/batchCreate`,
    data: {
      shopPointLinkId,
      amount,
    },
  })
}

// 查詢餘額定期報表結果
export const GetBusinessDataAutoSheetRecords = async ({
  shopId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/businessDataAutoSheetRecords`,
    params: {
      shopPointKey,
    },
  })
}

// 查詢當前會員餘額總和
export const GetSumMemberBalance = async ({
  shopId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'GET',
    url: `/${shopId}/shopPointReport/sumMemberBalance`,
    params: {
      shopPointKey,
    },
  })
}

// 下載餘額定期報表
export const DownloadBusinessDataAutoSheetRecord = async ({
  shopId,
  businessDataAutoSheetRecordId,
  shopPointKey,
}) => {
  return await admin2({
    method: 'POST',
    url: `/${shopId}/shopPointReport/businessDataAutoSheetRecords/${businessDataAutoSheetRecordId}/download`,
    params: {
      shopPointKey,
    },
  })
}

// -- new shop point --

export const GetShopPointList = async ({
  shopId,
}) => {
  return await admin2({
    url: `/${shopId}/shopPoint/list`,
    method: 'GET',
  })
}

export const GetShopPointCount = async ({
  shopId,
}) => {
  return await admin2({
    url: `/${shopId}/shopPoint/count`,
    method: 'GET',
  })
}

export const FindShopPointByKey = async ({ shopId, key }) => {
  return await admin2({
    url: `/${shopId}/shopPoint/key/${key}`,
    method: 'GET',
  })
}

export const UpdateShopPointByKey = async ({
  shopId,
  key,
  name,
  unitName,
  daysUntilExpiration,
  annualSettlementDate,
  daysDiffExpirationNotify,
  daysDiffExpirationNotifyList,
  clientExpirationDisplayDays,
}) => {
  const data = {
    name,
    unitName,
  }

  if (daysUntilExpiration !== undefined) data.daysUntilExpiration = daysUntilExpiration
  if (annualSettlementDate !== undefined) data.annualSettlementDate = annualSettlementDate
  if (daysDiffExpirationNotify !== undefined) data.daysDiffExpirationNotify = daysDiffExpirationNotify
  if (daysDiffExpirationNotifyList !== undefined) data.daysDiffExpirationNotifyList = daysDiffExpirationNotifyList
  if (clientExpirationDisplayDays !== undefined) data.clientExpirationDisplayDays = clientExpirationDisplayDays

  return await admin2({
    url: `/${shopId}/shopPoint/key/${key}`,
    method: 'PUT',
    data,
  })
}

/**
 * @featureKey admin.shopPoint.creation
 * @description 新增店家點數錢包
 * @param {Object} params
 * @param {String} params.shopId - 店家ID
 * @param {String} params.name - 點數名稱(顯示名稱)
 * @param {String} params.unitName - 點數單位名稱
 * @param {Number} params.daysUntilExpiration - 點數有效天數
 * @param {String} params.annualSettlementDate - 年度到期日
 * @param {Number} params.daysDiffExpirationNotify - 到期通知發送天數（向下相容欄位，使用 daysDiffExpirationNotifyList[0]）
 * @param {'year' | 'none' | 'perRecord'} params.expirationType - 點數錢包到期類型
 * @param {String | null} params.shopCustomModuleId - 第三方模組ID
 * @param {'day' | 'month' | 'year'} [params.expirationUnit] - perRecord 類型的到期單位
 * @param {Number[]} [params.daysDiffExpirationNotifyList] - 多個到期通知發送天數（最多 2 個）
 * @param {Number[]} [params.clientExpirationDisplayDays] - 前端顯示即將到期的天數門檻列表（最多 4 個）
 */
export const CreateShopPoint = async ({
  shopId,
  name,
  unitName,
  expirationType,
  daysUntilExpiration,
  annualSettlementDate,
  daysDiffExpirationNotify,
  shopCustomModuleId,
  expirationUnit,
  daysDiffExpirationNotifyList,
  clientExpirationDisplayDays,
}) => {
  return await admin2({
    url: `/${shopId}/shopPoint`,
    method: 'POST',
    data: {
      name,
      unitName,
      expirationType,
      daysUntilExpiration,
      annualSettlementDate,
      daysDiffExpirationNotify,
      shopCustomModuleId,
      expirationUnit,
      daysDiffExpirationNotifyList,
      clientExpirationDisplayDays,
    },
  })
}

export const DeleteShopPoint = async ({
  shopId,
  id,
}) => {
  return await admin2({
    url: `/${shopId}/shopPoint/${id}`,
    method: 'DELETE',
  })
}

/**
 * @typedef {Object} shopPointModuleCreationConfig
 * @property {string} moduleType
 * @property {string} name
 * @property {Object} feature
 * @property {Boolean} feature.findPointWalletBalance
 * @property {Boolean} feature.findPointWalletRecord
 * @property {Boolean} feature.addPointWalletRecord
 * @property {Boolean} feature.usePointWalletRecord
 * @property {Boolean} enabled
 * @property {string} shopCustomModuleId
 */

/**
 * @featureKey admin.shopPoint.creationConfig
 * @description 查詢哪些第三方模組已啟用並可以綁定點數錢包
 * @param {Object} params
 * @param {String} params.shopId - 店家ID
 * @returns {Api2Response<{shopPointModuleCreationConfigs: shopPointModuleCreationConfig[]}>}
 */
export const GetShopPointCreationConfigList = async ({ shopId }) => {
  return await admin2({
    url: `/${shopId}/shopPoint/creationConfig`,
    method: 'GET',
  })
}
